import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { TradingOfferOperationComment } from 'types/TradingOfferOperation';

const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  return format(new Date(date), "dd MMM yyyy · HH:mm'h'", { locale: es });
};

const getCommentAuthorLabel = (comment: TradingOfferOperationComment) => {
  const u = comment.user;
  if (u && typeof u === 'object') {
    if (u.name) return u.name;
    if (u.email) return u.email;
  }
  if (typeof u === 'string' && u.length) {
    return u.length > 24 ? `${u.slice(0, 24)}…` : u;
  }
  return '—';
};

const getInitials = (comment: TradingOfferOperationComment) => {
  const label = getCommentAuthorLabel(comment);
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase() || 'U';
};

export type TradingOfferOperationDetailCommentsItemProps = {
  comment: TradingOfferOperationComment;
  isAdmin: boolean;
  isLoading: boolean;
  isRemoving: boolean;
  /** Called with the full comment when the user confirms deletion */
  // eslint-disable-next-line no-unused-vars -- callback param name in type (documentation)
  onRemove: (comment: TradingOfferOperationComment) => void;
};

const TradingOfferOperationDetailCommentsItem: FC<
  TradingOfferOperationDetailCommentsItemProps
> = ({ comment, isAdmin, isLoading, isRemoving, onRemove }) => {
  const { t } = useTranslate();

  const roleLabelText = useMemo(() => {
    switch (comment.role) {
      case 'BUYER':
        return t('Buyer');
      case 'SELLER':
        return t('Seller');
      case 'ADMIN':
        return t('Admin');
      default:
        return '';
    }
  }, [comment.role, t]);

  return (
    <li className="d-flex align-items-start py-3 border-bottom">
      <div className="user-avatar user-avatar-sm bg-info-dim mr-3 mt-1">
        <span>{getInitials(comment)}</span>
      </div>
      <div className="flex-grow-1 min-w-0">
        <div className="d-flex justify-content-between align-items-start flex-wrap ">
          <div className="d-flex align-items-center flex-wrap min-w-0">
            <strong className="fs-13px fw-bold mb-0">
              {getCommentAuthorLabel(comment)}
            </strong>
            {comment.role ? (
              <span className="badge badge-dim badge-pill badge-primary text-nowrap flex-shrink-0 ml-1">
                {roleLabelText}
              </span>
            ) : null}
          </div>
          <div className="d-flex align-items-center flex-shrink-0">
            <span className="text-soft fs-12px">
              {formatDateTime(comment.createdAt)}
            </span>
            {isAdmin ? (
              <button
                type="button"
                className="btn btn-xs btn-icon btn-light ml-1"
                title={t('Delete')}
                aria-label={t('Delete')}
                disabled={isRemoving || isLoading}
                onClick={() => onRemove(comment)}
              >
                <em className="icon ni ni-trash" />
              </button>
            ) : null}
          </div>
        </div>
        <p className="fs-13px mb-0 mt-1 text-break">{comment.text}</p>
      </div>
    </li>
  );
};

export default TradingOfferOperationDetailCommentsItem;
